import { useMutation, useQueryClient } from "@tanstack/react-query";
import supabase from "@/supabase";

interface ResultBreakdown {
  answers: Record<string, string>; // Question ID to Answer ID mapping
}

interface OrientationQuizData {
  applicationId: string;
  resultBreakdown: ResultBreakdown;
}

interface SubmitResponse {
  success: boolean;
  message?: string;
  data?: {
    assessment: any;
    passed: boolean;
    nextStage?: string;
  };
}

/**
 * Hook for submitting orientation quiz results via cloud function
 */
export const useOrientationQuizCloudFunctions = () => {
  const queryClient = useQueryClient();

  // Submit orientation quiz results
  const submitOrientationQuiz = useMutation({
    mutationFn: async ({
      applicationId,
      resultBreakdown,
    }: OrientationQuizData): Promise<SubmitResponse> => {
      if (!applicationId) {
        throw new Error("Application ID is required");
      }

      try {
        const { data, error } = await supabase.functions.invoke(
          "orientation-submit-orientation-quiz",
          {
            body: {
              applicationId,
              resultBreakdown,
            },
          }
        );

        if (error) {
          console.error("Error submitting orientation quiz:", error);
          try {
            // Try to parse the error response body to get the actual error message
            const parsedError = await error.context.json();
            return parsedError;
          } catch (parseError) {
            // If parsing fails, fall back to error.message or default message
            return {
              success: false,
              message: error.message || "Failed to submit orientation quiz",
            };
          }
        }

        return data;
      } catch (error) {
        console.error("Error in submitOrientationQuiz:", error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["application"] });
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });

  return {
    submitOrientationQuiz,
  };
};
